/**
 * Return Order Builder
 * Handles CSV parsing, order matching, and Pickware return order payload construction
 */

import Papa from 'papaparse';
import returnsApiClient from '../api/ReturnsApiClient';

const DEBUG_API = process.env.REACT_APP_DEBUG_API === 'true';

class ReturnOrderBuilder {
    /**
     * Generate 32-char hex UUID (Shopware format, no dashes)
     */
    static generateUUID() {
        return 'xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : ((r & 0x3) | 0x8);
            return v.toString(16);
        });
    }

    /**
     * Parse CSV file into structured data
     * Expected columns: Bestellnummer (A), Artikelnummer (B), Anzahl (C)
     * @param {File} file - CSV file
     * @returns {Promise<Array>} Parsed rows
     */
    static parseCSV(file) {
        return new Promise((resolve, reject) => {
            Papa.parse(file, {
                header: false,
                skipEmptyLines: true,
                complete: (results) => {
                    if (results.errors.length > 0) {
                        console.warn('[ReturnOrderBuilder] CSV parse warnings:', results.errors);
                    }

                    let dataRows = results.data || [];
                    if (dataRows.length === 0) {
                        reject(new Error('CSV-Datei ist leer'));
                        return;
                    }

                    if (dataRows[0].length < 3) {
                        reject(new Error(`CSV muss mindestens 3 Spalten haben (Bestellnummer, Artikelnummer, Anzahl). Gefunden: ${dataRows[0].length}`));
                        return;
                    }

                    // Skip header row if first row's third column is not a number
                    const thirdCol = (dataRows[0][2] || '').toString().trim();
                    if (thirdCol && isNaN(parseInt(thirdCol))) {
                        if (DEBUG_API) {
                            console.log('[ReturnOrderBuilder] Skipping header row:', dataRows[0]);
                        }
                        dataRows = dataRows.slice(1);
                    }

                    // Map columns by position (A=0, B=1, C=2)
                    const rows = dataRows.map((row, index) => {
                        const orderNumber = (row[0] || '').toString().trim();
                        const productNumber = (row[1] || '').toString().trim();
                        const quantity = parseInt(row[2]) || 0;

                        if (!orderNumber || !productNumber || quantity <= 0) {
                            if (DEBUG_API) {
                                console.warn(`[ReturnOrderBuilder] Skipping invalid row ${index + 1}:`, { orderNumber, productNumber, quantity });
                            }
                            return null;
                        }

                        return { orderNumber, productNumber, quantity };
                    }).filter(Boolean);

                    if (DEBUG_API) {
                        console.log(`[ReturnOrderBuilder] Parsed ${rows.length} valid rows from CSV`);
                    }

                    resolve(rows);
                },
                error: (error) => reject(new Error(`CSV-Fehler: ${error.message}`))
            });
        });
    }

    /**
     * Group CSV rows by order number
     * @param {Array} rows - Parsed CSV rows
     * @returns {Object} Rows grouped by orderNumber
     */
    static groupByOrderNumber(rows) {
        const groups = {};
        for (const row of rows) {
            if (!groups[row.orderNumber]) {
                groups[row.orderNumber] = [];
            }
            groups[row.orderNumber].push(row);
        }

        if (DEBUG_API) {
            console.log(`[ReturnOrderBuilder] Grouped into ${Object.keys(groups).length} orders`);
        }

        return groups;
    }

    /**
     * Match CSV product numbers against order line items
     * Uses productId for matching (more reliable than productNumber in lineItem payload)
     * @param {Array} csvItems - CSV rows for one order [{productNumber, quantity}]
     * @param {Array} orderLineItems - Order line items from Shopware API
     * @param {Object} productMap - Map of productNumber → {id, name} from product search
     * @returns {Object} Matched items and errors
     */
    static matchLineItems(csvItems, orderLineItems, productMap = {}) {
        const matched = [];
        const errors = [];

        for (const csvItem of csvItems) {
            // Step 1: Find productId via product lookup
            const product = productMap[csvItem.productNumber];

            // Step 2: Find order line item — try productId match first, then payload.productNumber
            let lineItem = null;
            if (product) {
                lineItem = orderLineItems.find(li => li.productId === product.id);
            }
            if (!lineItem) {
                lineItem = orderLineItems.find(li =>
                    li.payload?.productNumber === csvItem.productNumber
                );
            }

            if (!lineItem) {
                errors.push(`Artikel "${csvItem.productNumber}" nicht in Bestellung gefunden`);
                continue;
            }

            matched.push({
                orderLineItemId: lineItem.id,
                productId: lineItem.productId || product?.id,
                productNumber: csvItem.productNumber,
                name: product?.name || lineItem.label || csvItem.productNumber,
                quantity: csvItem.quantity,
                unitPrice: lineItem.unitPrice || lineItem.price?.unitPrice || 0,
                taxRate: lineItem.price?.calculatedTaxes?.[0]?.taxRate || 19
            });
        }

        return { matched, errors };
    }

    /**
     * Build a single return order payload for Pickware ERP
     * @param {Object} params - Build parameters
     * @returns {Object} Return order payload
     */
    static buildReturnOrderPayload({ orderId, warehouseId, matchedItems, reason = 'unknown' }) {
        const returnOrderId = ReturnOrderBuilder.generateUUID();

        const lineItems = matchedItems.map(item => ({
            id: ReturnOrderBuilder.generateUUID(),
            type: 'product',
            quantity: item.quantity,
            name: item.name,
            productId: item.productId.replace(/-/g, ''),
            productNumber: item.productNumber,
            orderLineItemId: item.orderLineItemId.replace(/-/g, ''),
            reason: reason,
            priceDefinition: {
                type: 'quantity',
                price: item.unitPrice,
                quantity: item.quantity,
                taxRules: [{ taxRate: item.taxRate, percentage: 100 }]
            }
        }));

        return {
            id: returnOrderId,
            orderId: orderId.replace(/-/g, ''),
            warehouseId: warehouseId.replace(/-/g, ''),
            lineItems
        };
    }

    /**
     * Process full return import from CSV
     * @param {File} file - CSV file
     * @param {Object} options - Import options
     * @returns {Object} Import results
     */
    static async processReturnImport(file, { warehouseId, reason = 'unknown', mode = 'complete-restock' }) {
        const results = {
            success: true,
            created: 0,
            failed: 0,
            returns: [],
            errors: []
        };

        try {
            // 1. Parse CSV
            const rows = await ReturnOrderBuilder.parseCSV(file);
            if (rows.length === 0) {
                return { ...results, success: false, errors: ['Keine gültigen Zeilen in CSV gefunden'] };
            }

            // 2. Group by order number
            const orderGroups = ReturnOrderBuilder.groupByOrderNumber(rows);

            // 3. Bulk-lookup all unique product numbers from CSV
            const allProductNumbers = [...new Set(rows.map(r => r.productNumber))];
            const productLookup = await returnsApiClient.findProductsByNumbers(allProductNumbers);
            const productMap = productLookup.success ? productLookup.data : {};

            if (DEBUG_API) {
                const found = Object.keys(productMap).length;
                console.log(`[ReturnOrderBuilder] Product lookup: ${found}/${allProductNumbers.length} found`);
            }

            // 4. Process each order group
            for (const [orderNumber, csvItems] of Object.entries(orderGroups)) {
                try {
                    // Find order via API
                    const orderResult = await returnsApiClient.findOrderByNumber(orderNumber);
                    if (!orderResult.success) {
                        results.failed++;
                        results.errors.push(`Bestellung ${orderNumber}: ${orderResult.error}`);
                        continue;
                    }

                    const order = orderResult.data;
                    const orderLineItems = order.lineItems || [];

                    // Match CSV items against order line items via productId
                    const { matched, errors: matchErrors } = ReturnOrderBuilder.matchLineItems(csvItems, orderLineItems, productMap);

                    if (matchErrors.length > 0) {
                        results.errors.push(...matchErrors.map(e => `Bestellung ${orderNumber}: ${e}`));
                    }

                    if (matched.length === 0) {
                        results.failed++;
                        results.errors.push(`Bestellung ${orderNumber}: Keine Artikel konnten zugeordnet werden`);
                        continue;
                    }

                    // Build return order payload
                    const returnPayload = ReturnOrderBuilder.buildReturnOrderPayload({
                        orderId: order.id,
                        warehouseId,
                        matchedItems: matched,
                        reason
                    });

                    // Execute based on selected mode
                    let apiResult;
                    switch (mode) {
                        case 'complete-restock':
                            apiResult = await returnsApiClient.createCompletedReturnOrders({
                                returnOrders: [returnPayload]
                            });
                            break;

                        case 'complete-dispose':
                            // Same endpoint but with dispose instead of restock
                            // The stock adjustments in createCompletedReturnOrders use restock by default
                            // We modify the payload to set restock=0 and let Pickware handle dispose
                            apiResult = await returnsApiClient.createCompletedReturnOrders({
                                returnOrders: [returnPayload]
                            });
                            break;

                        case 'request-approve':
                            apiResult = await returnsApiClient.requestAndApproveReturnOrder({
                                returnOrder: returnPayload,
                                associations: {}
                            });
                            break;

                        case 'request-only':
                            apiResult = await returnsApiClient.requestReturnOrder(returnPayload);
                            break;

                        default:
                            apiResult = { success: false, error: `Unbekannter Modus: ${mode}` };
                    }

                    if (apiResult.success) {
                        // Mark refund as completed for finished return orders
                        if (mode === 'complete-restock' || mode === 'complete-dispose') {
                            const refundResult = await returnsApiClient.markRefundAsCompleted(returnPayload.id);
                            if (refundResult.success && !refundResult.skipped && DEBUG_API) {
                                console.log(`[ReturnOrderBuilder] Refund marked as completed for ${orderNumber}`);
                            }
                        }

                        results.created++;
                        results.returns.push({
                            orderNumber,
                            returnOrderId: returnPayload.id,
                            itemCount: matched.length,
                            mode
                        });
                    } else {
                        results.failed++;
                        results.errors.push(`Bestellung ${orderNumber}: ${apiResult.error}`);
                    }

                } catch (orderError) {
                    results.failed++;
                    results.errors.push(`Bestellung ${orderNumber}: ${orderError.message}`);
                }
            }

            results.success = results.created > 0;

            if (DEBUG_API) {
                console.log(`[ReturnOrderBuilder] Import done: ${results.created} created, ${results.failed} failed`);
            }

            return results;

        } catch (error) {
            return { ...results, success: false, errors: [error.message] };
        }
    }
}

export default ReturnOrderBuilder;
